<template>
  <div class="bg-white p-4 rounded-lg shadow mt-4">
    <div
      class="flex justify-between items-center mb-3 cursor-pointer"
      @click="toggleAttachments"
    >
      <h3 class="text-lg font-medium text-gray-900">
        Attachments: ({{ localAttachments.length }})
      </h3>
      <svg
        class="w-5 h-5 text-gray-500 transition-transform duration-200"
        :class="{ 'transform rotate-180': attachmentsExpanded }"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path
          fill-rule="evenodd"
          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
          clip-rule="evenodd"
        />
      </svg>
    </div>

    <div v-show="attachmentsExpanded" class="space-y-2">
      <div
        v-for="(attachment, index) in localAttachments"
        :key="'attachment-' + index"
        class="flex items-center p-2 hover:bg-gray-50 rounded-md group"
      >
        <!-- File Icon -->
        <svg
          class="h-5 w-5 text-gray-400 mr-3"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M8 4a3 3 0 00-3 3v4a5 5 0 0010 0V7a1 1 0 112 0v4a7 7 0 11-14 0V7a5 5 0 0110 0v4a3 3 0 11-6 0V7a1 1 0 012 0v4a1 1 0 102 0V7a3 3 0 00-3-3z"
            clip-rule="evenodd"
          />
        </svg>

        <!-- Attachment Name & Link -->
        <div class="flex-1 min-w-0">
          <a
            :href="attachment.url"
            target="_blank"
            rel="noopener noreferrer"
            class="text-sm text-brand-700 hover:text-brand-800 hover:underline truncate"
            :title="attachment.name || 'Attachment'"
          >
            {{ truncateText(attachment.name || "Attachment", 45) }}
          </a>

          <!-- File Size -->
          <span v-if="attachment.size" class="text-xs text-gray-500 ml-2">
            ({{ formatFileSize(attachment.size) }})
          </span>
        </div>

        <!-- Refresh Button -->
        <button
          @click="refreshAttachment(attachment, index)"
          class="ml-2 p-1 text-gray-500 hover:text-brand-600 opacity-0 group-hover:opacity-100 transition-opacity"
          title="Refresh expired link"
        >
          <svg
            class="w-4 h-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </button>
      </div>

      <p
        v-if="localAttachments.length === 0"
        class="text-sm text-gray-500 italic"
      >
        No attachments available
      </p>
    </div>
  </div>
</template>

<script>
import { ref } from "vue";
import { useStore } from "vuex";
import { truncateText } from "@/helpers/text"; 

export default {
  name: "RFIAttachments",
  props: {
    attachments: {
      type: Array,
      default: () => [],
      validator: (value) => {
        return value.every((item) => item.url);
      },
    },
    initiallyExpanded: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {
    const store = useStore();
    const attachmentsExpanded = ref(props.initiallyExpanded);
    const localAttachments = ref([...props.attachments]);
    const toggleAttachments = () => {
      attachmentsExpanded.value = !attachmentsExpanded.value;
    };

    const formatFileSize = (bytes) => {
      if (!bytes) return "";
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / 1048576).toFixed(1)} MB`;
    };

 

    const refreshAttachment = async (attachment, index) => {
      try {
        const newUrl = await store.dispatch("getNewURL", {
          attachmentId: attachment.id,
        });
        localAttachments.value[index].url = newUrl.url;
      } catch (error) {
        console.error("Failed to refresh attachment:", error);
      }
    };

    return {
      attachmentsExpanded,
      localAttachments,
      toggleAttachments,
      formatFileSize,
      truncateText,
      refreshAttachment,
    };
  },
};
</script>

<style scoped>
.v-enter-active,
.v-leave-active {
  transition: opacity 0.2s ease, max-height 0.2s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
  max-height: 0;
}

.v-enter-to,
.v-leave-from {
  opacity: 1;
  max-height: 500px;
}
</style>